import { forwardRef } from "react";

// material-ui
import { Box, Button, Divider, Typography } from "@mui/material";
import BackButton from "../BackButton";

interface MainCardProps {
  border?: boolean;
  boxShadow?: boolean;
  children: React.ReactNode;
  content?: boolean;
  contentClass?: string;
  contentSX?: object;
  darkTitle?: boolean;
  secondary?: React.ReactNode;
  shadow?: string;
  sx?: object;
  title?: React.ReactNode;
  actionButton?: {
    text: string;
    color: "primary" | "secondary" | "success" | "error" | "info" | "warning";
    onClick: () => void;
  };
  actionButtons?: {
    text: string;
    color: "primary" | "secondary" | "success" | "error" | "info" | "warning";
    onClick: () => void;
  }[];
  buttonChildren?: React.ReactNode;
  backText?: string;
  onBack?: () => void;
}

/**
 * The frame around a screen's content - despite the name, no longer a card.
 *
 * It used to render a rounded, bordered `<Card>` on a grey page, so every one
 * of the ~40 screens that mount it looked like a panel inside the panel. It is
 * a page header now: a title, the screen's actions, a rule, and the content
 * flush beneath at full width. `border` still draws a hairline outline for the
 * few screens that group two sections on one page.
 *
 * **Action colours are decided here, not by the caller.** Screens pass
 * `color: "success"` for "Add New Movie" - green, on a page that also had a
 * cyan Filter button - because there was nowhere else to say "this is the main
 * action". The first action is the brand button, the rest are outlined, and an
 * explicit `error` still means destructive.
 */
const MainCard = forwardRef(
  (
    {
      border = false,
      // Kept in the signature so the ~40 callers that still pass them do not
      // spread an unknown prop onto a DOM node. Elevation is not part of the
      // design any more: a page does not float.
      boxShadow,
      shadow,
      darkTitle,
      children,
      content = true,
      contentClass = "",
      contentSX = {},
      secondary,
      sx = {},
      title,
      actionButton,
      actionButtons,
      buttonChildren,
      backText,
      onBack,
      ...others
    }: MainCardProps,
    ref: any
  ) => {
    const actions = [...(actionButton ? [actionButton] : []), ...(actionButtons || [])];

    return (
      <>
        {backText && (
          <BackButton
            text={backText}
            onClick={onBack}
          />
        )}

        <Box
          ref={ref}
          {...others}
          sx={{
            width: "100%",
            ...(border && {
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              p: 3,
            }),
            ...sx,
          }}
        >
          {(title || actions.length > 0 || buttonChildren || secondary) && (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                {title &&
                  (typeof title === "string" ? (
                    <Typography
                      variant="h3"
                      sx={{ lineHeight: 1.25 }}
                    >
                      {title}
                    </Typography>
                  ) : (
                    title
                  ))}
                {secondary}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                {actions.map((button, index) => (
                  <Button
                    key={"action-button-" + index}
                    variant={index === 0 || button.color === "error" ? "contained" : "outlined"}
                    color={button.color === "error" ? "error" : "primary"}
                    onClick={button.onClick}
                  >
                    {button.text}
                  </Button>
                ))}
                {buttonChildren}
              </Box>
            </Box>
          )}

          {title && (
            <Divider
              sx={{
                mb: 3,
                ...(border && { mx: -3 }),
              }}
            />
          )}

          {content ? (
            <Box
              sx={contentSX}
              className={contentClass}
            >
              {children}
            </Box>
          ) : (
            children
          )}
        </Box>
      </>
    );
  }
);

export default MainCard;
